import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import ConfirmationModal from "../admin/ConfirmationModal";

function HeaderTailwind() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [dropdowns, setDropdowns] = useState({
    event: false,
    about: false,
    results: false,
    adminDashboard: false,
    adminManagement: false,
    adminOrganization: false,
    adminResults: false,
    adminUsers: false
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  
  // Initialisiere lang direkt aus URL oder i18n, um Flackern zu vermeiden
  const getInitialLang = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    const i18nLang = i18n.language?.split('-')[0] || i18n.language || "de";
    return urlLang || i18nLang || "de";
  };
  const [lang, setLang] = useState(getInitialLang);
  
  const dropdownRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const profileRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);

  useEffect(() => {
    const isDark = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  }, []);

  // Synchronisiere lang State mit i18n.language und location (ohne Flackern)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    const i18nLang = i18n.language?.split('-')[0] || i18n.language || "de";
    
    // Priorität: URL-Parameter > i18n.language > "de"
    const currentLang = urlLang || i18nLang || "de";
    
    if (currentLang !== lang) {
      setLang(currentLang);
    }
  }, [location.search, i18n.language, lang]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignoriere Klicks auf Links - diese sollen die Navigation nicht verhindern
      if (event.target.closest('a')) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdowns({
          event: false,
          about: false,
          results: false,
          adminDashboard: false,
          adminManagement: false,
          adminOrganization: false,
          adminResults: false,
          adminUsers: false
        });
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Schließe Mobile-Menu und Dropdowns wenn sich die Route ändert
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdowns({
      event: false,
      about: false,
      results: false,
      adminDashboard: false,
      adminManagement: false,
      adminOrganization: false,
      adminResults: false,
      adminUsers: false
    });
  }, [location.pathname]);

  // Scroll-Verhalten für Header (nur auf Mobile)
  useEffect(() => {
    const handleScroll = () => {
      // Nur auf Mobile aktivieren
      if (window.innerWidth >= 768) {
        setHeaderVisible(true);
        return;
      }

      const currentScrollY = window.scrollY;
      
      // Am oberen Rand immer sichtbar
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        setIsScrolled(false);
      } else {
        setIsScrolled(true);
        // Beim Scrollen nach unten verstecken, beim Scrollen nach oben zeigen
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setHeaderVisible(false);
        } else if (currentScrollY < lastScrollY) {
          setHeaderVisible(true);
        }
      }
      
      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      // Bei Größenänderung Header wieder anzeigen
      if (window.innerWidth >= 768) {
        setHeaderVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [lastScrollY]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleDropdown = (dropdown) => {
    setDropdowns(prev => ({
      event: dropdown === "event" ? !prev.event : false,
      about: dropdown === "about" ? !prev.about : false,
      results: dropdown === "results" ? !prev.results : false,
      adminDashboard: dropdown === "adminDashboard" ? !prev.adminDashboard : false,
      adminManagement: dropdown === "adminManagement" ? !prev.adminManagement : false,
      adminOrganization: dropdown === "adminOrganization" ? !prev.adminOrganization : false,
      adminResults: dropdown === "adminResults" ? !prev.adminResults : false,
      adminUsers: dropdown === "adminUsers" ? !prev.adminUsers : false
    }));
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem("darkMode", newDarkMode);
    // Tailwind erwartet 'dark' Klasse, nicht 'dark-mode'
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode'); // Für CSS-Variablen
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setProfileOpen(false);
    setLogoutConfirmOpen(false);
    navigate("/admin");
  };

  const changeLanguage = (langCode) => {
    setLang(langCode);
    const url = new URL(window.location);
    url.searchParams.set("lang", langCode);
    window.history.pushState({}, "", url);
    i18n.changeLanguage(langCode);
  };

  const scrollToTop = (e) => {
    // Verhindere nicht die Standard-Navigation
    setMobileMenuOpen(false);
    setProfileOpen(false);
    // Schließe alle Dropdowns
    setDropdowns({
      event: false,
      about: false,
      results: false,
      adminDashboard: false,
      adminManagement: false,
      adminOrganization: false,
      adminResults: false,
      adminUsers: false
    });
    // Scroll nur wenn wir auf derselben Seite bleiben
    if (location.pathname === window.location.pathname) {
      window.scrollTo(0, 0);
    }
  };

  const handleHashClick = (e) => {
    setMobileMenuOpen(false);
    // HashLink kümmert sich selbst um die Navigation
  };

  // Verwende immer den Admin Header für eingeloggte User, unabhängig von der Rolle
  const isAdmin = isAuthenticated;

  // Permission-Map für Menüpunkte
  const menuPermissions = {
    "/admin/dashboard": ["view_dashboard"],
    "/admin/participants": ["manage_participants"],
    "/admin/nations": ["manage_nations"],
    "/admin/categories": ["manage_categories"],
    "/admin/groups": ["manage_groups"],
    "/admin/schedule": ["manage_schedule"],
    "/admin/results": ["view_results", "manage_results"],
    "/admin/medal-table": ["view_results"],
    "/admin/mvp-table": ["view_results"],
    "/admin/users": ["manage_users"],
    "/admin/user-roles": ["manage_roles"],
    "/admin/settings": ["manage_schedule_settings", "manage_data"]
  };

  // Prüft, ob der User die benötigten Permissions hat
  const hasPermission = (path) => {
    if (!isAuthenticated || !user) return false;
    
    const normalizeRole = (role) => {
      if (!role) return '';
      return role.toLowerCase().replace(/[-_]/g, '');
    };
    
    const userRole = normalizeRole(user?.role);
    // Superadmin hat automatisch alle Permissions
    if (userRole === 'superadmin') return true;
    
    const requiredPermissions = menuPermissions[path];
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    
    const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
    return requiredPermissions.some((permission) => userPermissions.includes(permission));
  };

  // Filtert Menüpunkte basierend auf Permissions
  const filterMenuItems = (items) => {
    return items.filter((item) => {
      if (item.type === "link" && item.to) {
        return hasPermission(item.to);
      }
      if (item.type === "dropdown" && item.items) {
        const filteredItems = filterMenuItems(item.items);
        // Dropdown nur anzeigen, wenn mindestens ein Item sichtbar ist
        return filteredItems.length > 0;
      }
      return true;
    }).map((item) => {
      if (item.type === "dropdown" && item.items) {
        return {
          ...item,
          items: filterMenuItems(item.items)
        };
      }
      return item;
    });
  };

  const adminMenu = [
    {
      type: "link",
      to: "/admin/dashboard",
      icon: "fa-tachometer-alt",
      label: t("admin.menu.dashboard", "Dashboard"),
      isActive: isActive("/admin/dashboard"),
      onClick: scrollToTop
    },
    {
      type: "dropdown",
      label: t("admin.menu.management", "Verwaltung"),
      icon: "fa-cogs",
      isActive: isActive("/admin/participants") || isActive("/admin/nations") || isActive("/admin/categories"),
      dropdownKey: "adminManagement",
      open: dropdowns.adminManagement,
      items: [
        {
          type: "link",
          to: "/admin/participants",
          icon: "fa-users",
          label: t("admin.menu.participants", "Teilnehmer"),
          isActive: isActive("/admin/participants"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/nations",
          icon: "fa-flag",
          label: t("admin.menu.nations", "Nationen"),
          isActive: isActive("/admin/nations"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/categories",
          icon: "fa-list",
          label: t("admin.menu.categories", "Kategorien"),
          isActive: isActive("/admin/categories"),
          onClick: scrollToTop
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.organization", "Organisation"),
      icon: "fa-calendar-alt",
      isActive: isActive("/admin/groups") || isActive("/admin/schedule") || isActive("/caller") || (location.pathname === "/admin/settings" && new URLSearchParams(location.search).get("view") === "org"),
      dropdownKey: "adminOrganization",
      open: dropdowns.adminOrganization,
      items: [
        {
          type: "link",
          to: "/admin/groups",
          icon: "fa-layer-group",
          label: t("admin.menu.groups", "Gruppen"),
          isActive: isActive("/admin/groups"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/schedule",
          icon: "fa-calendar-alt",
          label: t("admin.menu.schedule", "Zeitplan"),
          isActive: isActive("/admin/schedule"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/caller",
          icon: "fa-bullhorn",
          label: t("admin.menu.caller", "Caller"),
          isActive: isActive("/caller"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "caller" || user.role === "admin" || user.role === "superadmin")
        },
        {
          type: "link",
          to: "/admin/settings?view=org",
          icon: "fa-sliders-h",
          label: t("admin.menu.settingsSchedule", "Zeitplan-Konfiguration"),
          isActive: location.pathname === "/admin/settings" && new URLSearchParams(location.search).get("view") === "org",
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.results", "Ergebnisse"),
      icon: "fa-clipboard-list",
      isActive: isActive("/admin/results") || isActive("/admin/medal-table") || isActive("/admin/mvp-table") || isActive("/scorekeeper"),
      dropdownKey: "adminResults",
      open: dropdowns.adminResults,
      show: isAuthenticated && user && user.role !== "caller",
      items: [
        {
          type: "link",
          to: "/admin/results",
          icon: "fa-clipboard-list",
          label: t("admin.menu.results", "Ergebnisse"),
          isActive: isActive("/admin/results"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/medal-table",
          icon: "fa-medal",
          label: t("admin.menu.medalTable", "Medaillenspiegel"),
          isActive: isActive("/admin/medal-table"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/mvp-table",
          icon: "fa-trophy",
          label: t("admin.menu.mvpTable", "MVP-Tabelle"),
          isActive: isActive("/admin/mvp-table"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/scorekeeper",
          icon: "fa-edit",
          label: t("admin.menu.scorekeeper", "Punkteerfassung"),
          isActive: isActive("/scorekeeper"),
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "scorekeeper" || user.role === "admin" || user.role === "superadmin")
        }
      ]
    },
    {
      type: "dropdown",
      label: t("admin.menu.users", "Benutzer"),
      icon: "fa-user-cog",
      isActive: location.pathname.startsWith("/admin/users") || location.pathname.startsWith("/admin/user-roles") || (location.pathname === "/admin/settings" && new URLSearchParams(location.search).get("view") === "user"),
      dropdownKey: "adminUsers",
      open: dropdowns.adminUsers,
      show: isAuthenticated && user && user.role === "superadmin",
      items: [
        {
          type: "link",
          to: "/admin/users",
          icon: "fa-users-cog",
          label: t("admin.menu.usersOverview", "Benutzerübersicht"),
          isActive: location.pathname === "/admin/users",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/user-roles",
          icon: "fa-key",
          label: t("admin.menu.userRoles", "Nutzerrollen"),
          isActive: location.pathname === "/admin/user-roles",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/admin/settings?view=user",
          icon: "fa-cog",
          label: t("admin.menu.settingsData", "Datenmanagement"),
          isActive: location.pathname === "/admin/settings" && new URLSearchParams(location.search).get("view") === "user",
          onClick: scrollToTop,
          show: isAuthenticated && user && (user.role === "admin" || user.role === "superadmin")
        }
      ]
    }
  ];

  const mainMenu = [
    {
      type: "link",
      to: "/",
      icon: "fa-home",
      label: t("header.home"),
      isActive: isActive("/"),
      onClick: scrollToTop
    },
    {
      type: "dropdown",
      label: t("header.event"),
      icon: "fa-calendar-alt",
      isActive: isActive("/taolu") || isActive("/chinwoo") || location.hash === "#schedule" || location.hash === "#location",
      dropdownKey: "event",
      open: dropdowns.event,
      items: [
        {
          type: "link",
          to: "/taolu",
          icon: "fa-fist-raised",
          label: t("header.tournamentFormat"),
          isActive: location.pathname === "/taolu",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/chinwoo",
          icon: "fa-dragon",
          label: t("header.kungFuCulture"),
          isActive: location.pathname === "/chinwoo",
          onClick: scrollToTop
        },
        {
          type: "hashlink",
          to: "/#schedule",
          icon: "fa-calendar-alt",
          label: t("header.schedule"),
          isActive: location.hash === "#schedule",
          onClick: handleHashClick
        },
        {
          type: "hashlink",
          to: "/#location",
          icon: "fa-map-marker-alt",
          label: t("header.location"),
          isActive: location.hash === "#location",
          onClick: handleHashClick
        }
      ]
    },
    {
      type: "dropdown",
      label: t("header.information"),
      icon: "fa-info-circle",
      isActive: isActive("/sponsors-partners") || isActive("/sponsors") || isActive("/downloads"),
      dropdownKey: "about",
      open: dropdowns.about,
      items: [
        {
          type: "link",
          to: "/sponsors-partners",
          icon: "fa-handshake",
          label: t("header.sponsorsPartners"),
          isActive: location.pathname === "/sponsors-partners",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/sponsors",
          icon: "fa-heart",
          label: t("header.sponsor"),
          isActive: location.pathname === "/sponsors",
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/downloads",
          icon: "fa-download",
          label: t("downloads.title"),
          isActive: location.pathname === "/downloads",
          onClick: scrollToTop
        }
      ]
    },
    // Ergebnisse-Menüpunkt nur in Development anzeigen
    ...(process.env.NODE_ENV !== 'production' ? [{
      type: "dropdown",
      label: t("header.results", "Ergebnisse"),
      icon: "fa-clipboard-list",
      isActive: isActive("/results") || isActive("/medal-table") || isActive("/mvp-table"),
      dropdownKey: "results",
      open: dropdowns.results,
      items: [
        {
          type: "link",
          to: "/results",
          icon: "fa-clipboard-list",
          label: t("header.allResults", "Alle Ergebnisse"),
          isActive: isActive("/results"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/medal-table",
          icon: "fa-medal",
          label: t("header.medalTable", "Medaillenspiegel"),
          isActive: isActive("/medal-table"),
          onClick: scrollToTop
        },
        {
          type: "link",
          to: "/mvp-table",
          icon: "fa-trophy",
          label: t("header.mvpTable", "MVP-Tabelle"),
          isActive: isActive("/mvp-table"),
          onClick: scrollToTop
        }
      ]
    }] : []),
    {
      type: "hashlink",
      to: "/#contact",
      icon: "fa-envelope",
      label: t("header.contact"),
      isActive: location.hash === "#contact",
      onClick: handleHashClick
    }
  ];

  const filteredAdminMenu = isAdmin && isAuthenticated ? filterMenuItems(adminMenu) : adminMenu;
  const menuItems = isAdmin ? filteredAdminMenu : mainMenu;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-700 transition-all duration-300 md:translate-y-0 ${
      headerVisible ? 'translate-y-0' : '-translate-y-full'
    } ${isScrolled ? 'shadow-md' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" onClick={scrollToTop} className="flex items-center gap-3">
            <img
              src="/images/logo.png"
              alt="Kung Fu Weltmeisterschaft Logo"
              className="h-10 w-10 object-contain"
            />
            <span 
              className="text-xl font-bold bg-gradient-to-r from-black via-red-600 to-yellow-500 bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(to right, #000000 0%, #000000 33%, #DC2626 33%, #DC2626 66%, #EAB308 66%, #EAB308 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              BER26
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden header-break:flex items-center gap-1" ref={dropdownRef}>
            {menuItems.filter(item => item.show !== false).map((item, idx) => (
              <div key={idx} className="relative">
                {item.type === "link" && (
                  <Link
                    to={item.to}
                    onClick={item.onClick}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      item.isActive
                        ? 'bg-primary dark:bg-primary-light text-white'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                )}
                {item.type === "hashlink" && (
                  <HashLink
                    to={item.to}
                    onClick={item.onClick}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      item.isActive
                        ? 'bg-primary dark:bg-primary-light text-white'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </HashLink>
                )}
                {item.type === "dropdown" && (
                  <div className="relative">
                    <button
                      onClick={() => toggleDropdown(item.dropdownKey)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        item.isActive
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                      <i className={`fas fa-chevron-${item.open ? "up" : "down"} text-xs`}></i>
                    </button>
                    {item.open && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1">
                        {item.items.filter(subItem => subItem.show !== false).map((subItem, subIdx) => (
                          <div key={subIdx}>
                            {subItem.type === "link" && (
                              <Link
                                to={subItem.to}
                                onClick={subItem.onClick}
                                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                                  subItem.isActive
                                    ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                }`}
                              >
                                <i className={`fas ${subItem.icon}`}></i>
                                <span>{subItem.label}</span>
                              </Link>
                            )}
                            {subItem.type === "hashlink" && (
                              <HashLink
                                to={subItem.to}
                                onClick={subItem.onClick}
                                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                                  subItem.isActive
                                    ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                }`}
                              >
                                <i className={`fas ${subItem.icon}`}></i>
                                <span>{subItem.label}</span>
                              </HashLink>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Controls */}
          <div className="hidden header-break:flex items-center gap-4">
            {/* Spenden Button */}
            <HashLink
              to="/sponsors#private-donation"
              onClick={handleHashClick}
              className="flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white rounded-full text-sm font-medium transition-all hover:bg-primary-dark dark:hover:bg-primary shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20"
            >
              <i className="fas fa-heart"></i>
              <span>{t("header.donate", "Spenden")}</span>
            </HashLink>
            {/* Nur im Admin-Bereich Profile anzeigen (Login-Button entfernt aus Sicherheitsgründen) */}
            {isAdmin && isAuthenticated && (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen((prev) => !prev)}
                  className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 ${
                    profileOpen ? 'bg-primary dark:bg-primary-light text-white border-primary dark:border-primary-light shadow-lg shadow-primary/20' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <i className="fas fa-user text-sm"></i>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden z-20">
                    <div className="px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {user?.username || t("auth.user", "Nutzer")}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 uppercase">
                        {user?.role?.toUpperCase() || ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLogoutConfirmOpen(true)}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <i className="fas fa-sign-out-alt text-slate-500 dark:text-slate-400"></i>
                      <span>{t("header.logout", "Abmelden")}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Language Selector */}
            <div className="flex items-center gap-2">
              <div
                onClick={() => changeLanguage("de")}
                className={`cursor-pointer rounded-full transition-all ${
                  lang === "de" ? 'ring-2 ring-primary dark:ring-primary-light shadow-md' : ''
                }`}
              >
                <img
                  src="/images/flags/DE.png"
                  alt="Deutsch"
                  className={`w-6 h-6 object-cover rounded-full transition-all ${
                    lang === "de" 
                      ? 'opacity-100 grayscale-0 brightness-110' 
                      : 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0 hover:brightness-110 hover:shadow-lg'
                  }`}
                />
              </div>
              <div
                onClick={() => changeLanguage("en")}
                className={`cursor-pointer rounded-full transition-all ${
                  lang === "en" ? 'ring-2 ring-primary dark:ring-primary-light shadow-md' : ''
                }`}
              >
                <img
                  src="/images/flags/GB.png"
                  alt="English"
                  className={`w-6 h-6 object-cover rounded-full transition-all ${
                    lang === "en" 
                      ? 'opacity-100 grayscale-0 brightness-110' 
                      : 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0 hover:brightness-110 hover:shadow-lg'
                  }`}
                />
              </div>
              <div
                onClick={() => changeLanguage("zh")}
                className={`cursor-pointer rounded-full transition-all ${
                  lang === "zh" ? 'ring-2 ring-primary dark:ring-primary-light shadow-md' : ''
                }`}
              >
                <img
                  src="/images/flags/CN.png"
                  alt="Chinese"
                  className={`w-6 h-6 object-cover rounded-full transition-all ${
                    lang === "zh" 
                      ? 'opacity-100 grayscale-0 brightness-110' 
                      : 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0 hover:brightness-110 hover:shadow-lg'
                  }`}
                />
              </div>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              className="w-10 h-6 bg-slate-200 dark:bg-slate-700 rounded-full relative transition-colors"
            >
              <div
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  darkMode ? 'translate-x-4' : ''
                }`}
              >
                <i
                  className={`fas ${darkMode ? 'fa-moon' : 'fa-sun'} text-xs absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
                    darkMode ? 'text-slate-600' : 'text-yellow-500'
                  }`}
                ></i>
              </div>
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="header-break:hidden p-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="header-break:hidden pb-4 border-t border-slate-200 dark:border-slate-700 pt-4">
            <nav className="space-y-1">
              {menuItems.filter(item => item.show !== false).map((item, idx) => (
                <div key={idx}>
                  {item.type === "link" && (
                    <Link
                      to={item.to}
                      onClick={() => {
                        // Schließe Mobile-Menu
                        // Navigation wird von React Router gehandhabt
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        item.isActive
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  )}
                  {item.type === "hashlink" && (
                    <HashLink
                      to={item.to}
                      onClick={handleHashClick}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        item.isActive
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </HashLink>
                  )}
                  {item.type === "dropdown" && (
                    <div>
                      <button
                        onClick={() => toggleDropdown(item.dropdownKey)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          item.isActive
                            ? 'bg-primary dark:bg-primary-light text-white'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <i className={`fas ${item.icon}`}></i>
                          <span>{item.label}</span>
                        </div>
                        <i className={`fas fa-chevron-${item.open ? "up" : "down"} text-xs`}></i>
                      </button>
                      {item.open && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.items.filter(subItem => subItem.show !== false).map((subItem, subIdx) => (
                            <div key={subIdx}>
                              {subItem.type === "link" && (
                                <Link
                                  to={subItem.to}
                                  onClick={subItem.onClick}
                                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                                    subItem.isActive
                                      ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100'
                                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                  }`}
                                >
                                  <i className={`fas ${subItem.icon}`}></i>
                                  <span>{subItem.label}</span>
                                </Link>
                              )}
                              {subItem.type === "hashlink" && (
                                <HashLink
                                  to={subItem.to}
                                  onClick={(e) => {
                                    // Schließe Dropdown und Mobile-Menu
                                    setDropdowns(prev => ({
                                      ...prev,
                                      [item.dropdownKey]: false
                                    }));
                                    setMobileMenuOpen(false);
                                    // Rufe subItem.onClick auf, wenn vorhanden (z.B. handleHashClick)
                                    if (subItem.onClick) {
                                      subItem.onClick(e);
                                    }
                                    // HashLink kümmert sich selbst um die Navigation
                                  }}
                                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                                    subItem.isActive
                                      ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100'
                                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                  }`}
                                >
                                  <i className={`fas ${subItem.icon}`}></i>
                                  <span>{subItem.label}</span>
                                </HashLink>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Controls */}
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  onClick={() => changeLanguage("de")}
                  className={`cursor-pointer rounded-full transition-all ${
                    lang === "de" ? 'ring-2 ring-primary shadow-md' : ''
                  }`}
                >
                  <img 
                    src="/images/flags/DE.png" 
                    alt="Deutsch" 
                    className={`w-6 h-6 object-cover rounded-full transition-all ${
                      lang === "de" 
                        ? 'opacity-100 grayscale-0 brightness-110' 
                        : 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0 hover:brightness-110 hover:shadow-lg'
                    }`} 
                  />
                </div>
                <div
                  onClick={() => changeLanguage("en")}
                  className={`cursor-pointer rounded-full transition-all ${
                    lang === "en" ? 'ring-2 ring-primary shadow-md' : ''
                  }`}
                >
                  <img 
                    src="/images/flags/GB.png" 
                    alt="English" 
                    className={`w-6 h-6 object-cover rounded-full transition-all ${
                      lang === "en" 
                        ? 'opacity-100 grayscale-0 brightness-110' 
                        : 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0 hover:brightness-110 hover:shadow-lg'
                    }`} 
                  />
                </div>
                <div
                  onClick={() => changeLanguage("zh")}
                  className={`cursor-pointer rounded-full transition-all ${
                    lang === "zh" ? 'ring-2 ring-primary shadow-md' : ''
                  }`}
                >
                  <img 
                    src="/images/flags/CN.png" 
                    alt="Chinese" 
                    className={`w-6 h-6 object-cover rounded-full transition-all ${
                      lang === "zh" 
                        ? 'opacity-100 grayscale-0 brightness-110' 
                        : 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0 hover:brightness-110 hover:shadow-lg'
                    }`} 
                  />
                </div>
              </div>
              <button
                onClick={toggleDarkMode}
                className="w-10 h-6 bg-slate-200 dark:bg-slate-700 rounded-full relative transition-colors"
              >
                <div
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    darkMode ? 'translate-x-4' : ''
                  }`}
                >
                  <i
                    className={`fas ${darkMode ? 'fa-moon' : 'fa-sun'} text-xs absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
                      darkMode ? 'text-slate-600' : 'text-yellow-500'
                    }`}
                  ></i>
                </div>
              </button>
              {/* Spenden Button - Mobile */}
              <HashLink
                to="/sponsors#private-donation"
                onClick={(e) => {
                  handleHashClick(e);
                  setMobileMenuOpen(false);
                }}
                className="ml-4 flex items-center gap-2 px-3 py-2 rounded-full bg-primary dark:bg-primary-light text-white text-sm font-medium transition-all hover:bg-primary-dark dark:hover:bg-primary shadow-sm hover:shadow-md"
              >
                <i className="fas fa-heart"></i>
                <span>{t("header.donate", "Spenden")}</span>
              </HashLink>
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={() => setLogoutConfirmOpen(true)}
                  className="ml-4 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900 dark:bg-slate-800 text-white text-sm font-medium transition-colors hover:bg-slate-800 dark:hover:bg-slate-700"
                >
                  <i className="fas fa-user-shield"></i>
                  <span className="flex flex-col items-start leading-tight">
                    {user?.username || t("auth.user", "Nutzer")}
                    <span className="text-[10px] uppercase opacity-80">
                      {user?.role?.toUpperCase() || ""}
                    </span>
                  </span>
                  <i className="fas fa-sign-out-alt text-xs"></i>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
        title={t("header.logoutConfirm.title", "Abmelden")}
        subtitle={t("header.logoutConfirm.subtitle", "Möchtest du dich wirklich abmelden?")}
        confirmText={t("header.logoutConfirm.confirm", "Abmelden")}
        cancelText={t("header.logoutConfirm.cancel", "Abbrechen")}
        type="danger"
        layout="inline"
      />
    </header>
  );
}

export default HeaderTailwind;
